// Feature 08: Link Impact Metric Changes Table

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Mouse,
  Visibility,
  TouchApp,
  LocationOn,
} from '@mui/icons-material';
import { MetricChange } from '../../types/linkImpact.types';

interface LinkImpactMetricsTableProps {
  metricChanges: MetricChange[];
  compact?: boolean;
}

const METRIC_CONFIG: Record<string, {
  label: string;
  icon: React.ReactNode;
  formatter: (value: number) => string;
  betterDirection: 'up' | 'down';
}> = {
  clicks: {
    label: 'Clicks',
    icon: <Mouse fontSize="small" />,
    formatter: (v) => v.toLocaleString(),
    betterDirection: 'up',
  },
  impressions: {
    label: 'Impressions',
    icon: <Visibility fontSize="small" />,
    formatter: (v) => v.toLocaleString(),
    betterDirection: 'up',
  },
  ctr: {
    label: 'CTR',
    icon: <TouchApp fontSize="small" />,
    formatter: (v) => `${(v * 100).toFixed(2)}%`,
    betterDirection: 'up',
  },
  position: {
    label: 'Position',
    icon: <LocationOn fontSize="small" />,
    formatter: (v) => v.toFixed(1),
    betterDirection: 'down', // Lower position is better
  },
};

const getTrendIcon = (delta: number, betterDirection: 'up' | 'down') => {
  if (Math.abs(delta) < 0.01) return <TrendingFlat fontSize="small" />;
  
  const isImproving = betterDirection === 'up' ? delta > 0 : delta < 0;
  return isImproving ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />;
};

const getTrendColor = (delta: number, betterDirection: 'up' | 'down'): 'success' | 'error' | 'default' => {
  if (Math.abs(delta) < 0.01) return 'default';
  
  const isImproving = betterDirection === 'up' ? delta > 0 : delta < 0;
  return isImproving ? 'success' : 'error';
};

const LinkImpactMetricsTable: React.FC<LinkImpactMetricsTableProps> = ({
  metricChanges,
  compact = false,
}) => {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size={compact ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            <TableCell>Metric</TableCell>
            <TableCell align="right">Baseline (Before)</TableCell>
            <TableCell align="right">Outcome (After)</TableCell>
            <TableCell align="right">Change</TableCell>
            <TableCell align="center">Trend</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {metricChanges.map((change) => {
            const config = METRIC_CONFIG[change.metric];
            const percentChange = change.percentChange;
            const trendColor = getTrendColor(change.delta, config.betterDirection);

            return (
              <TableRow key={change.metric}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {config.icon}
                    <Typography variant="body2">{config.label}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  {config.formatter(change.baseline)}
                </TableCell>
                <TableCell align="right">
                  {config.formatter(change.outcome)}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)}%`}
                    color={trendColor}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {getTrendIcon(change.delta, config.betterDirection)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Box p={1} bgcolor="grey.50">
        <Typography variant="caption" color="text.secondary">
          * Position: Lower is better • Click metrics show traffic impact
        </Typography>
      </Box>
    </TableContainer>
  );
};

export default LinkImpactMetricsTable;
